import cx from 'classnames';
import type { FC, HTMLAttributes, MouseEvent } from 'react';

import { Icon, Typography } from 'src/components-v2/DataDisplay';
import { Link } from 'src/components/Inputs';

import { Image } from 'src/components-v2/Media';
import { Logo } from '../assets';
import { CardWIcon, CardWImage, isCardWIcon, isCardWImage } from '../types';

import { useTrack } from 'src/contexts';
import { Events } from 'src/lib/trackingEvents';
import styles from './Card.module.scss';

type CardProps = HTMLAttributes<HTMLAnchorElement> & {
  iconSize?: 18 | 20;
  iconBoxSize?: 32 | 40 | 48;
} & (CardWIcon | CardWImage);

export const Card: FC<CardProps> = (props) => {
  const {
    className,
    href,
    title,
    description,
    iconSize = 18,
    iconBoxSize = 48,
    locale,
    trackingId,
    ...rest
  } = props;

  const track = useTrack();

  const textPart = (
    <>
      <span className={styles.Card__title}>
        <Typography
          theme='main-menu-new'
          variant='p'
          component='span'
          sx={{ mb: 0 }}
        >
          {title}
        </Typography>
      </span>

      {description && (
        <Typography
          className={styles.Card__description}
          theme='main-menu-new'
          variant='p'
          component='span'
        >
          {description}
        </Typography>
      )}
    </>
  );

  if (isCardWImage(props)) {
    const ProductShot = {
      linkDemo: (
        <Image
          src='https://images.ctfassets.net/ss5kfr270og3/12v1ZRQBMolDRjOtObvzKP/16cea9928bb6abf118570358c053bc94/link-demo.png?fm=webp&q=70'
          width={550}
          height={408}
          alt=''
        />
      ),
      featured: (
        <Image
          src='https://images.ctfassets.net/ss5kfr270og3/1zzbqadlk6nFfGn5C6aNYI/c921f8669f2448908c94a5d46de415ae/fr-nav-image.png?fm=webp&q=70'
          width={236}
          height={165}
          alt='Fall Product Release 2026'
        />
      ),
      link: (
        <Image
          src='https://images.ctfassets.net/ss5kfr270og3/3cwo45cB6RAF8SF2ISkU76/7e6c811c75dae8cc3a36def4bc4dd153/2d6fbbe4a73d62962570837e3a1961c8050ef2f5.png?fm=webp&q=70&w=275'
          srcset='https://images.ctfassets.net/ss5kfr270og3/3cwo45cB6RAF8SF2ISkU76/7e6c811c75dae8cc3a36def4bc4dd153/2d6fbbe4a73d62962570837e3a1961c8050ef2f5.png?fm=webp&q=70&w=275 1x,
          https://images.ctfassets.net/ss5kfr270og3/3cwo45cB6RAF8SF2ISkU76/7e6c811c75dae8cc3a36def4bc4dd153/2d6fbbe4a73d62962570837e3a1961c8050ef2f5.png?fm=webp&q=70&w=550 2x'
          width={550}
          height={408}
          alt=''
        />
      ),
      layer: (
        <Image
          src='https://images.ctfassets.net/ss5kfr270og3/22VCpQwf4jvwXTXsQ4fjGj/8f032b2248eca23cbdfd3f17d286a3fc/82536257d5b15d133e81ae0d93714bf9da34e9c5.png?fm=webp&q=70&w=275'
          srcset='https://images.ctfassets.net/ss5kfr270og3/22VCpQwf4jvwXTXsQ4fjGj/8f032b2248eca23cbdfd3f17d286a3fc/82536257d5b15d133e81ae0d93714bf9da34e9c5.png?fm=webp&q=70&w=275 1x,
          https://images.ctfassets.net/ss5kfr270og3/22VCpQwf4jvwXTXsQ4fjGj/8f032b2248eca23cbdfd3f17d286a3fc/82536257d5b15d133e81ae0d93714bf9da34e9c5.png?fm=webp&q=70&w=550 2x'
          width={550}
          height={408}
          alt=''
        />
      ),
    };

    const imageTextPart = (
      <>
        <span className={styles.Card__title}>
          <Typography
            theme='main-menu-new'
            variant='p'
            component='span'
            sx={{ mb: 0 }}
          >
            {title}
          </Typography>
        </span>

        {description && (
          <Typography
            className={styles.Card__description}
            theme='main-menu-new'
            variant='p'
            component='span'
          >
            {description}
          </Typography>
        )}

        {props.cta && (
          <span className={styles.Card__cta}>
            {props.cta}
            <span className={styles.Card__ctaArrows}>
              <span className={styles.ctaArrowsInner}>
                <Icon icon='ArrowRight' size={14} />
                <Icon icon='ArrowRight' size={14} />
              </span>
            </span>
          </span>
        )}
      </>
    );

    const handleTrackableClick = (event: MouseEvent<HTMLAnchorElement>) => {
      if (!trackingId) {
        return;
      }

      track({
        type: Events.BUTTON_CLICKED,
        payload: {
          trackingId: Events.BUTTON_CLICKED,
          event,
          currentTarget: event.currentTarget,
          buttonName: trackingId,
          buttonHref: event.currentTarget.href,
        },
      });
    };

    return (
      <Link href={href} locale={locale}>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- We are correctly merging `onClick` with the parent `href`; ESLint just doesn't know about it */}
        <a
          onClick={handleTrackableClick}
          className={cx(
            styles.Card,
            styles.Card_image,
            { [styles.Card_description]: !!description },
            className,
          )}
          {...rest}
        >
          <span
            className={cx(
              styles.Card__image,
              styles[`Card__image_${props.imagePos}`],
            )}
          >
            <span className={styles.Image}>{ProductShot[props.image]}</span>
          </span>

          <span className={styles.Card__body}>{imageTextPart}</span>
        </a>
      </Link>
    );
  }

  if (isCardWIcon(props)) {
    const WrappedIcon =
      props.icon === 'PlaidLogo' ? (
        <Logo sx={{ width: `${iconSize}px` }} />
      ) : (
        <Icon icon={props.icon} size={iconSize} />
      );

    return (
      <Link href={href} locale={locale}>
        <a
          className={cx(
            styles.Card,
            styles.Card_icon,
            { [styles.Card_description]: !!description },
            className,
          )}
          {...rest}
        >
          <span
            className={cx(
              styles.Card__icon,
              styles[`Card__icon_${iconBoxSize}`],
            )}
          >
            {WrappedIcon}
          </span>
          <span className={styles.Card__body}>{textPart}</span>
        </a>
      </Link>
    );
  }

  return (
    <Link href={href} locale={locale}>
      <a className={cx(styles.Card, className)} {...rest}>
        {textPart}
      </a>
    </Link>
  );
};
